import type { Targeting, TargetingOperator } from '@orderlymeds/measurements-schemas';
import { TARGETING_OPERATORS } from '@orderlymeds/measurements-schemas';
import { useState } from 'react';
import styles from './TargetingBuilder.module.css';

interface TargetingBuilderProps {
	value: Targeting | null;
	onChange: (targeting: Targeting | null) => void;
	disabled?: boolean;
}

interface RuleRow {
	key: string;
	attribute: string;
	operator: TargetingOperator;
	value: string; // always stored as string in the UI; parsed on output
}

const OPERATOR_LABELS: Record<TargetingOperator, string> = {
	eq: 'equals',
	neq: 'not equals',
	in: 'in list',
	nin: 'not in list',
	gt: 'greater than',
	lt: 'less than',
	gte: 'at least',
	lte: 'at most',
};

let ruleKeyCounter = 0;
function nextRuleKey() {
	ruleKeyCounter += 1;
	return `r${ruleKeyCounter}`;
}

function ruleValueToString(v: string | number | string[]): string {
	if (Array.isArray(v)) return v.join(', ');
	return String(v);
}

function parseRuleValue(operator: TargetingOperator, raw: string): string | number | string[] {
	if (operator === 'in' || operator === 'nin') {
		return raw
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
	}
	if (operator === 'gt' || operator === 'lt' || operator === 'gte' || operator === 'lte') {
		const n = parseFloat(raw);
		return Number.isNaN(n) ? raw : n;
	}
	return raw;
}

function targetingToRows(targeting: Targeting): RuleRow[] {
	return targeting.rules.map((r) => ({
		key: nextRuleKey(),
		attribute: r.attribute,
		operator: r.operator,
		value: ruleValueToString(r.value),
	}));
}

function rowsToTargeting(rows: RuleRow[], match: 'all' | 'any'): Targeting | null {
	const validRows = rows.filter((r) => r.attribute.trim());
	if (validRows.length === 0) return null;
	return {
		match,
		rules: validRows.map((r) => ({
			attribute: r.attribute.trim(),
			operator: r.operator,
			value: parseRuleValue(r.operator, r.value),
		})),
	};
}

export function TargetingBuilder({ value, onChange, disabled = false }: TargetingBuilderProps) {
	const [mode, setMode] = useState<'visual' | 'json'>('visual');
	const [match, setMatch] = useState<'all' | 'any'>(value?.match ?? 'all');
	const [rows, setRows] = useState<RuleRow[]>(value ? targetingToRows(value) : []);
	const [jsonText, setJsonText] = useState<string>(value ? JSON.stringify(value, null, 2) : '');
	const [jsonError, setJsonError] = useState<string | null>(null);

	// When switching to JSON mode, serialize current visual state
	function handleSwitchToJson() {
		const current = rowsToTargeting(rows, match);
		setJsonText(current ? JSON.stringify(current, null, 2) : '');
		setJsonError(null);
		setMode('json');
	}

	// When switching to visual mode, parse the JSON
	function handleSwitchToVisual() {
		if (jsonText.trim()) {
			try {
				const parsed = JSON.parse(jsonText.trim()) as Targeting;
				setRows(targetingToRows(parsed));
				setMatch(parsed.match ?? 'all');
				setJsonError(null);
			} catch {
				setJsonError('Invalid JSON — fix errors before switching to visual mode.');
				return;
			}
		} else {
			setRows([]);
		}
		setMode('visual');
	}

	function handleAddRule() {
		const newRows = [
			...rows,
			{ key: nextRuleKey(), attribute: '', operator: 'eq' as TargetingOperator, value: '' },
		];
		setRows(newRows);
		const targeting = rowsToTargeting(newRows, match);
		onChange(targeting);
	}

	function handleRemoveRule(key: string) {
		const newRows = rows.filter((r) => r.key !== key);
		setRows(newRows);
		onChange(rowsToTargeting(newRows, match));
	}

	function handleRuleChange(key: string, field: keyof Omit<RuleRow, 'key'>, val: string) {
		const newRows = rows.map((r) => {
			if (r.key !== key) return r;
			if (field === 'operator') {
				return { ...r, operator: val as TargetingOperator };
			}
			return { ...r, [field]: val };
		});
		setRows(newRows);
		onChange(rowsToTargeting(newRows, match));
	}

	function handleMatchChange(m: 'all' | 'any') {
		setMatch(m);
		onChange(rowsToTargeting(rows, m));
	}

	function handleAddTargeting() {
		const newRows = [
			{ key: nextRuleKey(), attribute: '', operator: 'eq' as TargetingOperator, value: '' },
		];
		setRows(newRows);
		setMatch('all');
		onChange(rowsToTargeting(newRows, 'all'));
	}

	function handleClearTargeting() {
		setRows([]);
		setJsonText('');
		onChange(null);
	}

	function handleJsonBlur() {
		if (!jsonText.trim()) {
			setJsonError(null);
			onChange(null);
			return;
		}
		try {
			const parsed = JSON.parse(jsonText.trim()) as Targeting;
			setJsonError(null);
			onChange(parsed);
		} catch {
			setJsonError('Invalid JSON');
		}
	}

	// No-targeting empty state
	if (value === null && rows.length === 0 && mode === 'visual') {
		return (
			<div className={styles.wrapper}>
				<div className={styles.header}>
					<span className={styles.title}>Targeting Rules</span>
					<div className={styles.toggleGroup}>
						<button
							type="button"
							className={[styles.toggleBtn, styles.toggleActive].join(' ')}
							onClick={() => setMode('visual')}
							disabled={disabled}
						>
							Visual
						</button>
						<button
							type="button"
							className={styles.toggleBtn}
							onClick={handleSwitchToJson}
							disabled={disabled}
						>
							JSON
						</button>
					</div>
				</div>
				<div className={styles.emptyState}>
					<p className={styles.emptyText}>No targeting — all visitors included.</p>
					{!disabled && (
						<button type="button" className={styles.linkBtn} onClick={handleAddTargeting}>
							Add targeting
						</button>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<span className={styles.title}>Targeting Rules</span>
				<div className={styles.toggleGroup}>
					<button
						type="button"
						className={[styles.toggleBtn, mode === 'visual' ? styles.toggleActive : ''].join(' ')}
						onClick={mode === 'json' ? handleSwitchToVisual : undefined}
						disabled={disabled}
					>
						Visual
					</button>
					<button
						type="button"
						className={[styles.toggleBtn, mode === 'json' ? styles.toggleActive : ''].join(' ')}
						onClick={mode === 'visual' ? handleSwitchToJson : undefined}
						disabled={disabled}
					>
						JSON
					</button>
				</div>
			</div>

			{mode === 'json' ? (
				<div className={styles.jsonMode}>
					<textarea
						className={styles.jsonTextarea}
						value={jsonText}
						onChange={(e) => setJsonText(e.target.value)}
						onBlur={handleJsonBlur}
						rows={8}
						disabled={disabled}
						aria-label="Targeting rules (JSON)"
					/>
					{jsonError && <p className={styles.jsonError}>{jsonError}</p>}
				</div>
			) : (
				<div className={styles.visualMode}>
					<div className={styles.matchRow}>
						<span className={styles.matchLabel}>Match:</span>
						<label className={styles.radioLabel}>
							<input
								type="radio"
								name="targeting-match"
								value="all"
								checked={match === 'all'}
								onChange={() => handleMatchChange('all')}
								disabled={disabled}
							/>
							All rules must match
						</label>
						<label className={styles.radioLabel}>
							<input
								type="radio"
								name="targeting-match"
								value="any"
								checked={match === 'any'}
								onChange={() => handleMatchChange('any')}
								disabled={disabled}
							/>
							Any rule can match
						</label>
					</div>

					{rows.length > 0 && (
						<div className={styles.rulesTable}>
							<div className={styles.rulesHeader}>
								<span className={styles.colHeader}>Attribute</span>
								<span className={styles.colHeader}>Operator</span>
								<span className={styles.colHeader}>Value</span>
								<span />
							</div>
							{rows.map((row) => (
								<div key={row.key} className={styles.ruleRow}>
									<input
										className={[styles.ruleInput, styles.mono].join(' ')}
										type="text"
										value={row.attribute}
										onChange={(e) => handleRuleChange(row.key, 'attribute', e.target.value)}
										placeholder="e.g. device_type"
										disabled={disabled}
									/>
									<select
										className={styles.ruleSelect}
										value={row.operator}
										onChange={(e) => handleRuleChange(row.key, 'operator', e.target.value)}
										disabled={disabled}
									>
										{TARGETING_OPERATORS.map((op) => (
											<option key={op} value={op}>
												{OPERATOR_LABELS[op]}
											</option>
										))}
									</select>
									<input
										className={[styles.ruleInput, styles.mono].join(' ')}
										type={
											row.operator === 'gt' ||
											row.operator === 'lt' ||
											row.operator === 'gte' ||
											row.operator === 'lte'
												? 'number'
												: 'text'
										}
										value={row.value}
										onChange={(e) => handleRuleChange(row.key, 'value', e.target.value)}
										placeholder={
											row.operator === 'in' || row.operator === 'nin' ? 'CA, NY, TX' : 'value'
										}
										disabled={disabled}
									/>
									{!disabled && (
										<button
											type="button"
											className={styles.removeRuleBtn}
											onClick={() => handleRemoveRule(row.key)}
											aria-label="Remove rule"
										>
											✕
										</button>
									)}
								</div>
							))}
						</div>
					)}

					{!disabled && (
						<div className={styles.footer}>
							<button type="button" className={styles.addRuleBtn} onClick={handleAddRule}>
								+ Add rule
							</button>
							<button type="button" className={styles.clearBtn} onClick={handleClearTargeting}>
								Clear targeting
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
